import { formatDistanceToNow, startOfDay } from 'date-fns';
import { locale, dayOfWeekMapping } from '@helpers/constants';
import { de } from 'date-fns/locale';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import React from 'react';

export function transformUrlToPath(url) {
    if (!url) return url;

    //remove the protocol
    let result = url.replaceAll('https://', '');
    result = result.replaceAll('http://', '');

    //remove all special characters
    result = result.replaceAll(/\W/g, '-');

    //remove trailing slash
    /*  if (result?.endsWith('-')) {
     result = result.slice(0, -1);
   } */

    return result;
}

export function checkUrl(url) {
    try {
        // Block all non-string or empty inputs
        if (typeof url !== 'string' || !url.trim()) return false;

        const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
        const parsed = new URL(normalizedUrl);
        const hostname = parsed.hostname;

        // Block: empty or just dots
        if (!hostname || /^\.{1,}$/.test(hostname)) return false;

        // Block: localhost and similar (optional)
        if (!hostname.includes('.')) return false;

        // Block: contains spaces
        if (/\s/.test(url)) return false;

        // Block: hostname starts or ends with a dot or hyphen
        if (/^[-.]/.test(hostname) || /[-.]$/.test(hostname)) return false;

        // Block: multiple consecutive dots
        if (hostname.includes('..')) return false;

        // Block: invalid protocol (we only accept http/https)
        if (!/^https?:$/.test(parsed.protocol)) return false;

        // Optional: basic TLD check (e.g. .com, .net, .org, etc.)
        if (!/\.[a-z]{2,}$/i.test(hostname)) return false;

        return true;
    } catch (e) {
        return false;
    }
}

export function hexToRgb(hex) {
    // Convert hex color to RGB values
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return [r, g, b];
}

export function luminance(r, g, b) {
    var a = [r, g, b].map(function (v) {
        v /= 255;
        return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    });
    return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

export function contrast(hex1, hex2) {
    // Convert hex colors to RGB values
    const rgb1 = hexToRgb(hex1);
    const rgb2 = hexToRgb(hex2);

    var lum1 = luminance(rgb1[0], rgb1[1], rgb1[2]);
    var lum2 = luminance(rgb2[0], rgb2[1], rgb2[2]);
    var brightest = Math.max(lum1, lum2);
    var darkest = Math.min(lum1, lum2);
    return ((brightest + 0.05) / (darkest + 0.05)).toFixed(1);
}

export function colorForContrast(value1) {
    if (value1) {
        return value1 > 7 ? '#006400' : value1 > 4.5 ? '#4ac828' : 'red';
    }
}

export function capitalizeFirstLetter(string) {
    if (string && string.trim() !== '') return string.charAt(0).toUpperCase() + string.slice(1);
    return string;
}

export function getInitials(text) {
    const stringParts = text?.toUpperCase().split(' ').filter(Boolean);
    if (!stringParts || stringParts.length === 0) return '';
    return stringParts.length > 1
        ? `${stringParts[0][0]}${stringParts[stringParts.length - 1][0]}`
        : stringParts[0][0];
}

export function parseInputString(str) {
    const unitPx = str.slice(-2);
    const unitProc = str.slice(-1);
    if (unitPx === 'px') {
        const value = str.substr(0, str.length - 2);
        return [unitPx, value];
    } else if (unitProc === '%') {
        const value = str.substr(0, str.length - 1);
        return [unitProc, value];
    } else {
        return [false, false];
    }
}

export function displayBiggestTimeUnit(ms) {
    if (ms < 10000) return ms.toFixed(0) + ' ms';

    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
        return `${days} day${days > 1 ? 's' : ''}`;
    } else if (hours > 0) {
        return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
        return `${minutes} min`;
    } else {
        return `${seconds} sec`;
    }
}

export function displayBiggestFileUnit(bytes) {
    if (typeof bytes !== 'number') {
        return 'Invalid input';
    }

    const units = ['bytes', 'KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];

    for (let i = 0; i < units.length - 1; i++) {
        if (bytes < 1024) {
            // Convert to the next biggest unit if it's not in bytes
            if (i === 0) {
                return `${(bytes / 1024).toFixed(2)} ${units[i + 1]}`;
            } else {
                return `${bytes.toFixed(2)} ${units[i]}`;
            }
        }
        bytes /= 1024;
    }

    // Return YiB if input is extremely large
    return `${bytes.toFixed(2)} ${units[units.length - 1]}`;
}

export function isWholeNumber(str) {
    return /^-?\d+$/.test(str);
}

export function getHashCode(str) {
    var hash = 0,
        i,
        chr;
    if (str.length === 0) return hash;
    for (i = 0; i < str.length; i++) {
        chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export function readabilityScoreRating(score) {
    if (score < 30) return 0;
    else if (score < 50) return 1;
    else if (score < 60) return 2;
    else if (score < 70) return 3;
    else if (score < 80) return 4;
    else if (score < 90) return 5;
    else return 6;
}

export function deepEqual(object1, object2) {
    const keys1 = Object.keys(object1);
    const keys2 = Object.keys(object2);

    if (keys1.length !== keys2.length) {
        return false;
    }

    for (const key of keys1) {
        const val1 = object1[key];
        const val2 = object2[key];
        const areObjects = isObject(val1) && isObject(val2);
        if ((areObjects && !deepEqual(val1, val2)) || (!areObjects && val1 !== val2)) {
            return false;
        }
    }

    return true;
}

function isObject(object) {
    return object != null && typeof object === 'object';
}

export function diffBetween2Dates(Date1, Date2) {
    return Math.floor((Date2 - Date1) / (1000 * 60 * 60 * 24));
}

export function getTimeDifferenceFromNow(date, lang) {
    let options = {};
    if (lang === 'de') options = { locale: de };
    return formatDistanceToNow(date, { addSuffix: true, ...options });
}

export function isStartOfDaySameOrAfter(dateToCheck, referenceDate) {
    const dateWithoutTime = startOfDay(new Date(dateToCheck));
    const referenceWithoutTime = startOfDay(new Date(referenceDate));

    return dateWithoutTime >= referenceWithoutTime;
}

export function getClosestDate(date, allowedDaysStr) {
    if (!date || !allowedDaysStr) return undefined;
    // Parse the given date
    const dateObj = new Date(date);

    // Split the days string into an array
    const allowedDaysArr = allowedDaysStr.split(',');

    // Get the current day of the week (0 = Sunday, 6 = Saturday)
    const currentDay = dateObj.getDay();

    let minDiff = 7;

    // Find the closest day from the list
    for (const day of allowedDaysArr) {
        const targetDay = dayOfWeekMapping[day];
        const diff = (targetDay - currentDay + 7) % 7; // Handle negative diffs by wrapping around

        // If the day is the same as current day, no need to change
        if (diff === 0) {
            return dateObj; // Return the current date in YYYY-MM-DD format
        }

        // Track the closest day
        if (diff < minDiff) {
            minDiff = diff;
        }
    }

    // Add the difference to the current date to get the closest day
    dateObj.setDate(dateObj.getDate() + minDiff);

    // Return the resulting date
    return dateObj;
}

export function getFormatedDate(dateVal, lang = 'de', format = 'numeric') {
    if (!dateVal) return '';

    const dateObj = new Date(dateVal);
    var options = {};

    switch (format) {
        case 'numeric':
            options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
            };
            break;
        case 'numericWithTime':
            options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: undefined,
            };
            break;
        case 'numericWithPreciseTime':
            options = {
                year: 'numeric',
                month: 'numeric',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            };
            break;
        case 'short':
            options = {
                month: 'short',
                year: 'numeric',
            };
            break;
        case 'weekday':
            options = {
                weekday: 'long',
            };
            break;
    }

    return dateObj.toLocaleDateString(locale[lang] || lang, options);
}

export function parseTimeToDate(timeStr) {
    if (!timeStr) return null;
    const [h, m, s] = timeStr.split(':').map(Number);
    return new Date(1970, 0, 1, h, m, s);
}

export function formatDateToTime(date) {
    if (!date) return '00:00:00';
    const h = String(date.getHours()).padStart(2, '0');
    const m = String(date.getMinutes()).padStart(2, '0');
    const s = String(date.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
}

export function getLocalizedWeekdaysDict(lang = 'de') {
    const weekdays = Object.entries(dayOfWeekMapping)
        .sort(([, a], [, b]) => a - b)
        .map(([key]) => key);

    const mondayFirst = [...weekdays.slice(1), weekdays[0]];

    const baseDate = new Date(Date.UTC(2021, 5, 7)); // Monday, June 7, 2021

    const dict = {};
    for (let i = 0; i < 7; i++) {
        const date = new Date(baseDate);
        date.setDate(baseDate.getDate() + i);
        dict[mondayFirst[i]] = getFormatedDate(date, lang, 'weekday');
    }

    return dict;
}

export function dateOlderThan(date, minutes) {
    return Date.now() - new Date(date).getTime() > minutes * 60 * 1000; // Check if more than 1 hour ago
}

/* Function to change color for percentage difference in Graphs */
export function getColorForPercentDifference(percent, negativeIsGood) {
    if (percent === '-') return 'dark';
    if (percent === 0) return 'dark';
    else {
        if (negativeIsGood) return percent < 0 ? 'darkGreen' : 'darkRed';
        return percent < 0 ? 'darkRed' : 'darkGreen';
    }
}

// get the percentage difference between two numbers
export function getPercentageDifference(val1, val2) {
    if (!val1 || !val2 || !Number(val2)) return 0;
    const percentageDifference = ((val1 - val2) / val2) * 100;
    if (percentageDifference === 0) return 0;
    else return percentageDifference.toFixed(1);
}

export function flattenRow(row, columns, cellLimit = 32767) {
    let updatedColumns = { ...columns };

    const flattened = {};
    const expandedKeys = new Set();

    for (const key in row) {
        const value = row[key];

        if (value instanceof Date) {
            flattened[key] = value.toISOString();
        } else if (Array.isArray(value)) {
            const joined = value.join(',');

            if (joined.length > cellLimit) {
                let tempKey = '';
                for (let i = 0, part = 0; i < joined.length; i += cellLimit, part++) {
                    const chunk = joined.slice(i, i + cellLimit);

                    if (part === 0) {
                        flattened[key] = chunk;
                    } else {
                        tempKey = part > 0 ? `${key}_${part}` : key;
                        flattened[tempKey] = chunk;
                        updatedColumns[tempKey] = `${updatedColumns[key]} (${part})`;
                    }
                }
            } else {
                flattened[key] = joined;
            }
        } else if (typeof value === 'object' && value !== null) {
            flattened[key] = JSON.stringify(value);
            if (flattened[key]?.length > cellLimit) {
                flattened[key] = flattened[key].substring(0, cellLimit - 3) + '...';
            }
        } else {
            flattened[key] = value;

            if (flattened[key]?.length > cellLimit) {
                flattened[key] = flattened[key].substring(0, cellLimit - 3) + '...';
            }
        }
    }

    return [flattened, updatedColumns];
}

export function sanitizeHTMLSnippet(htmlString) {
    return parse(DOMPurify.sanitize(htmlString));
}

export function renderWithLineBreaks(text) {
    if (!text) return null;

    const lines = text.split('\n');
    const result = [];

    lines.forEach((line, idx) => {
        result.push(line);
        if (idx < lines.length - 1) {
            result.push(React.createElement('br', { key: `br-${idx}` }));
        }
    });

    return result;
}

/* Merges two sx functions/objects together while providing the theme prop to functions. */
export function mergeSx(baseSx, extraSx) {
    return theme => ({
        ...(typeof baseSx === 'function' ? baseSx(theme) : baseSx),
        ...(typeof extraSx === 'function' ? extraSx(theme) : extraSx),
    });
}
